import Axios from 'axios';
import { Form, Formik, FormikErrors, FormikProps } from 'formik';
import Moment from 'moment';
import React, { useEffect } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import Cookies from 'js-cookie';
import PDFObject from 'pdfobject';
import URLSearchParams from '@ungap/url-search-params';
import { WebShipmentRequest, WebShipmentRequestItem } from '@rosenau/book-shipment-models';
import { FormColumn, FormRow, FormSection, FormInputField, FormReadOnlyInputField, FormSelectField, SelectOption, FormCheckboxField, FormTextAreaField, FormDateField, FormTimeField, FormPages, FormPage, FormPostalCodeField, FormPhoneNumberField, FormCollection, FormCityPicker, getErrorMessage, getFormikErrorsFromValidationResult, withoutEmpty, LoadingSpinner, modalConfirmation } from '@rosenau/rosenau-ui';
import base64ToBlob from './utils/base64ToBlob';

const isIE = navigator.userAgent.indexOf("Trident/") > -1;

type InputField = new() => FormInputField<WebShipmentRequest>;
const InputField = FormInputField as InputField;

type ReadOnlyInputField = new() => FormReadOnlyInputField<WebShipmentRequest>;
const ReadOnlyInputField = FormReadOnlyInputField as ReadOnlyInputField;

type SelectField = new() => FormSelectField<WebShipmentRequest>;
const SelectField = FormSelectField as SelectField;

type CheckboxField = new() => FormCheckboxField<WebShipmentRequest>;
const CheckboxField = FormCheckboxField as CheckboxField;

type TextAreaField = new() => FormTextAreaField<WebShipmentRequest>;
const TextAreaField = FormTextAreaField as TextAreaField;

type DateField = new() => FormDateField<WebShipmentRequest>;
const DateField = FormDateField as DateField;

type TimeField = new() => FormTimeField<WebShipmentRequest>;
const TimeField = FormTimeField as TimeField;

type PostalCodeField = new() => FormPostalCodeField<WebShipmentRequest>;
const PostalCodeField = FormPostalCodeField as PostalCodeField;

type PhoneNumberField = new() => FormPhoneNumberField<WebShipmentRequest>;
const PhoneNumberField = FormPhoneNumberField as PhoneNumberField;

type CityPicker = new() => FormCityPicker<WebShipmentRequest>;
const CityPicker = FormCityPicker as CityPicker;

type Collection = new() => FormCollection<WebShipmentRequestItem>;
const Collection = FormCollection as Collection;

interface BookShipmentFormObserverProps {
    formik: FormikProps<WebShipmentRequest>;
    dirty: boolean;
    updateDirty: (dirty: boolean) => void;
}

const BookShipmentFormObserver = (props: BookShipmentFormObserverProps) => {
    useEffect(() => {
        if (props.dirty !== props.formik.dirty) {
            props.updateDirty(props.formik.dirty);
        }
    }, [props.formik, props.formik.dirty]);

    return null;
};

export interface BookShipmentFormProps {
    environment: string;
    bookShipmentEndpointURL: string;
    quoteRequestsEndpointURL: string;
    renderButtons?: boolean;
}

export interface BookShipmentFormState {
    loaded: boolean;
    loading: boolean;
    data?: WebShipmentRequest;
    yesNoOptions?: SelectOption[];
    chargeTypeOptions?: SelectOption[];
    provinceOptions?: SelectOption[];
    countryOptions?: SelectOption[];
    freightTypeOptions?: SelectOption[];
    weightUnitOptions?: SelectOption[];
    dimensionUnitOptions?: SelectOption[];
    dangerousGoodsClassOptions?: SelectOption[];
    dangerousGoodsPackingGroupOptions?: SelectOption[];
    placardTypeOptions?: SelectOption[];
    errorMessage?: React.ReactNode;
    formComplete: boolean;
    formDirty: boolean;
    currentPage?: number;
    pageCount?: number;
}

export default class BookShipmentForm<P extends BookShipmentFormProps = BookShipmentFormProps> extends React.Component<P, BookShipmentFormState> {
    ref: React.RefObject<HTMLDivElement>;
    formPagesRef: React.RefObject<FormPages>;
    urlSearchParams: URLSearchParams = new URLSearchParams(window.location.search);

    constructor(props: Readonly<P>) {
        super(props);

        this.state = {
            loaded: false,
            yesNoOptions: [
                { value: true, description: "Yes" },
                { value: false, description: "No" }
            ],
            chargeTypeOptions: [
                { value: "P", description: "Prepaid" },
                { value: "C", description: "Collect" },
                { value: "G", description: "3rd Party" }
            ],
            provinceOptions: [
                { value: "AB", description: "AB" },
                { value: "BC", description: "BC" },
                { value: "MB", description: "MB" },
                { value: "NB", description: "NB" },
                { value: "NL", description: "NL" },
                { value: "NT", description: "NT" },
                { value: "NS", description: "NS" },
                { value: "NU", description: "NU" },
                { value: "ON", description: "ON" },
                { value: "PE", description: "PE" },
                { value: "QC", description: "QC" },
                { value: "SK", description: "SK" },
                { value: "YT", description: "YT" }
            ],
            freightTypeOptions: [
                { value: "Pallet", description: "Pallet" },
                { value: "Tote", description: "Tote" },
                { value: "Drum", description: "Drum" },
                { value: "Bundle", description: "Bundle" },
                { value: "Box", description: "Box" },
                { value: "Pail", description: "Pail" },
                { value: "Crate", description: "Crate" },
                { value: "Envelope", description: "Envelope" },
                { value: "Other", description: "Other" }
            ],
            weightUnitOptions: [
                { value: "L", description: "lbs" },
                { value: "K", description: "kg" }
            ],
            dimensionUnitOptions: [
                { value: "I", description: "in" },
                { value: "F", description: "ft" },
                { value: "C", description: "cm" },
                { value: "M", description: "m" }
            ],
            dangerousGoodsClassOptions: [
                { value: "1.1", description: "1.1" },
                { value: "1.2", description: "1.2" },
                { value: "1.3", description: "1.3" },
                { value: "1.4", description: "1.4" },
                { value: "1.5", description: "1.5" },
                { value: "1.6", description: "1.6" },
                { value: "2.1", description: "2.1" },
                { value: "2.2", description: "2.2" },
                { value: "2.3", description: "2.3" },
                { value: "2", description: "2" },
                { value: "3", description: "3" },
                { value: "4.1", description: "4.1" },
                { value: "4.2", description: "4.2" },
                { value: "4.3", description: "4.3" },
                { value: "5.1", description: "5.1" },
                { value: "5.2", description: "5.2" },
                { value: "6.1", description: "6.1" },
                { value: "6.2", description: "6.2" },
                { value: "8", description: "8" },
                { value: "9", description: "9" }
            ],
            dangerousGoodsPackingGroupOptions: [
                { value: "N/A", description: "N/A" },
                { value: "I", description: "I" },
                { value: "II", description: "II" },
                { value: "III", description: "III" }
            ],
            placardTypeOptions: [
                { value: "Explosive", description: "Explosive (Class 1)" },
                { value: "Gases", description: "Gases (Class 2)" },
                { value: "Flammable Liquids", description: "Flammable Liquids (Class 3)" },
                { value: "Flammable Solids", description: "Flammable Solids (Class 4)" },
                { value: "Oxidizing Substances", description: "Oxidizing Substances (Class 5)" },
                { value: "Toxic & Infectious", description: "Toxic & Infectious (Class 6)" },
                { value: "Radioactive", description: "Radioactive (Class 7)" },
                { value: "Corrosive", description: "Corrosive (Class 8)" },
                { value: "Miscellaneous", description: "Miscellaneous (Class 9)" }
            ],
            formComplete: false,
            formDirty: false,
            loading: false
        };

        this.ref = React.createRef();
        this.formPagesRef = React.createRef();
    }

    componentDidUpdate(_oldProps: BookShipmentFormProps, _oldState: BookShipmentFormState) {
        if (this.state.formComplete && this.ref.current) {
            const message = this.ref.current.querySelector(".confirmation-message");

            if (message) {
                scrollIntoView(message, { behavior: "smooth", scrollMode: "if-needed", block: "center" });
            }
        }
    }

    async componentDidMount() {
        if (this.urlSearchParams.has("code")) {
            await this.loadFromQuoteRequest();
        } else {
            this.setState({
                loaded: true
            });
        }
    }

    render(): React.ReactNode {
        return <Formik
            initialValues={this.state.data || this.createEmptyModel()}
            onSubmit={this.submit.bind(this)}
            validateOnBlur={false}
            validateOnChange={false}
            validate={this.validate.bind(this)}>
            {formik => <Form>
                {this.renderFormLayout(formik)}
            </Form>}
        </Formik>
    }

    renderFormLayout(formik: FormikProps<WebShipmentRequest>) {
        if (!this.state.loaded) {
            if (!this.state.loading) {
                return null;
            } else {
                return this.renderLoadingIndicator();
            }
        }

        const disabledForQuoteRequest = !!this.state.data;

        return (
            <div ref={this.ref}>
                <BookShipmentFormObserver formik={formik} dirty={this.state.formDirty} updateDirty={dirty => this.setState({ formDirty: dirty })} />
                <style type="text/css">
                    {`@-webkit-keyframes spinner-border {
                        to {
                            -webkit-transform: rotate(360deg);
                            transform: rotate(360deg);
                        }
                    }

                    @keyframes spinner-border {
                        to {
                            -webkit-transform: rotate(360deg);
                            transform: rotate(360deg);
                        }
                    }

                    .spinner-border {
                        display: inline-block;
                        width: 2rem;
                        height: 2rem;
                        vertical-align: text-bottom;
                        border: .25em solid currentColor;
                        border-right-color: transparent;
                        border-radius: 50%;
                        -webkit-animation: .75s linear infinite spinner-border;
                        animation: .75s linear infinite spinner-border;
                    }`}
                </style>
                {!this.state.formComplete ? <FormPages formik={formik} errorMessage={this.state.errorMessage} ref={this.formPagesRef} renderButtons={this.props.renderButtons} updateCurrentPage={page => this.setState({ currentPage: page })} updatePageCount={pageCount => this.setState({ pageCount: pageCount })}>
                    <FormPage>
                        <FormSection title="Your contact information">
                            <FormRow>
                                <FormColumn columns={12}>
                                    <InputField labelText="Name" name="contactName" type="text" maxLength={35} required={true} />
                                </FormColumn>
                            </FormRow>
                            <FormRow>
                                <FormColumn columns={12}>
                                    <InputField labelText="Email" name="contactEmail" type="email" maxLength={80} required={true} />
                                </FormColumn>
                            </FormRow>
                            <FormRow>
                                <FormColumn columns={8}>
                                    <PhoneNumberField labelText="Phone" name="contactPhoneNumber" required={true} />
                                </FormColumn>
                                <FormColumn columns={4}>
                                    <InputField labelText="Extension" name="contactPhoneExtension" type="text" maxLength={5} />
                                </FormColumn>
                            </FormRow>
                        </FormSection>
                        <FormSection title="Freight charges">
                            <FormRow>
                                {this.state.chargeTypeOptions &&
                                    <FormColumn columns={6}>
                                        <SelectField labelText="Freight charges" name="chargeType" options={this.state.chargeTypeOptions} />
                                    </FormColumn>
                                }
                                {formik.values.chargeType === "G" &&
                                    <FormColumn columns={6}>
                                        <InputField labelText="3rd party account #" name="customerNumber" type="text" maxLength={6} required={true} />
                                    </FormColumn>
                                }
                            </FormRow>
                        </FormSection>
                    </FormPage>
                    <FormPage>
                        <FormSection title="Pickup information (shipper)">
                            <FormRow>
                                <FormColumn columns={12}>
                                    <InputField labelText="Company name" name="shipperName" type="text" maxLength={35} required={true} />
                                </FormColumn>
                            </FormRow>
                            <FormRow>
                                <FormColumn columns={12}>
                                    <SelectField labelText="Are you the point of contact for the pickup location?" name="submitterIsContact" options={this.state.yesNoOptions} fieldColumns={6} />
                                </FormColumn>
                            </FormRow>
                            {formik.values.submitterIsContact.toString() === "false" && <React.Fragment>
                                <FormRow>
                                    <FormColumn columns={12}>
                                        <InputField labelText="Pickup point of contact name" name="shipperContactName" type="text" maxLength={35} required={true} />
                                    </FormColumn>
                                </FormRow>
                                <FormRow>
                                    <FormColumn columns={12}>
                                        <InputField labelText="Pickup contact email" name="shipperEmail" type="email" maxLength={80} />
                                    </FormColumn>
                                </FormRow>
                                <FormRow>
                                    <FormColumn columns={8}>
                                        <PhoneNumberField labelText="Pickup contact phone" name="shipperPhoneNumber" required={true} />
                                    </FormColumn>
                                    <FormColumn columns={4}>
                                        <InputField labelText="Extension" name="shipperPhoneExtension" type="text" maxLength={5} />
                                    </FormColumn>
                                </FormRow>
                            </React.Fragment>}
                            <FormRow>
                                <FormColumn columns={12}>
                                    <InputField labelText="Street address" name="shipperAddress" type="text" maxLength={55} required={true} />
                                </FormColumn>
                            </FormRow>
                            <FormRow>
                                <FormColumn columns={12}>
                                    <InputField name="shipperAddress2" type="text" maxLength={55} />
                                </FormColumn>
                            </FormRow>
                            <FormRow>
                                <CityPicker environment={process.env.REACT_APP_Environment} formik={formik} cityField="shipperCity" provinceField="shipperProvince" required={true} disabled={disabledForQuoteRequest} />
                                <FormColumn columns={4}>
                                    <PostalCodeField labelText="Postal code" name="shipperPostalCode" type="text" maxLength={7} required={true} />
                                </FormColumn>
                            </FormRow>
                            <FormRow>
                                <FormColumn columns={12} style={{marginBottom: '0'}}>
                                    <label htmlFor="preferredPickupDate">Preferred pickup window <span className="d-inline-block ml-1 text-danger" title="{Preferred pickup window is required.}">*</span></label>
                                </FormColumn>
                            </FormRow>
                            <FormRow>
                                <FormColumn columns={4}>
                                    <DateField helpText="Date" name="preferredPickupDate" minDate={Moment().startOf("day").toDate()} required={true} />
                                </FormColumn>
                                <FormColumn columns={4}>
                                    <TimeField helpText="Ready time" name="preferredPickupReadyTime" minTime={Moment("09:00", "HH:mm").toDate()} maxTime={Moment("17:00", "HH:mm").toDate()} required={true} />
                                </FormColumn>
                                <FormColumn columns={4}>
                                    <TimeField helpText="Close time" name="preferredPickupCloseTime" minTime={formik.values.preferredPickupReadyTime ? new Date(Math.min(Moment(formik.values.preferredPickupReadyTime, "HH:mm").add(2, "hour").toDate().getTime(), Moment("17:00", "HH:mm").toDate().getTime())) : Moment("17:00", "HH:mm").toDate()} maxTime={Moment("17:00", "HH:mm").toDate()} required={true} />
                                </FormColumn>
                            </FormRow>
                        </FormSection>
                    </FormPage>
                    <FormPage>
                        <FormSection title="Delivery information (consignee)">
                            <FormRow>
                                <FormColumn columns={12}>
                                    <InputField labelText="Company name" name="consigneeName" type="text" maxLength={35} required={true} />
                                </FormColumn>
                            </FormRow>
                            <FormRow>
                                <FormColumn columns={12}>
                                    <InputField labelText="Consignee point of contact name" name="consigneeContactName" type="text" maxLength={35} required={true} />
                                </FormColumn>
                            </FormRow>
                            <FormRow>
                                <FormColumn columns={12}>
                                    <InputField labelText="Street address" name="consigneeAddress" type="text" maxLength={55} required={true} />
                                </FormColumn>
                            </FormRow>
                            <FormRow>
                                <FormColumn columns={12}>
                                    <InputField name="consigneeAddress2" type="text" maxLength={55} />
                                </FormColumn>
                            </FormRow>
                            <FormRow>
                                <CityPicker environment={process.env.REACT_APP_Environment} formik={formik} cityField="consigneeCity" provinceField="consigneeProvince" required={true} disabled={disabledForQuoteRequest} />
                                <FormColumn columns={4}>
                                    <PostalCodeField labelText="Postal code" name="consigneePostalCode" type="text" maxLength={15} required={true} />
                                </FormColumn>
                            </FormRow>
                            <FormRow>
                                <FormColumn columns={8}>
                                    <PhoneNumberField labelText="Phone number" name="consigneePhoneNumber" required={true} />
                                </FormColumn>
                                <FormColumn columns={4}>
                                    <InputField labelText="Extension" name="consigneePhoneExtension" type="text" maxLength={5} />
                                </FormColumn>
                            </FormRow>
                            <FormRow>
                                <FormColumn columns={12}>
                                    <CheckboxField labelText="Delivery appointment required" name="appointmentRequired" />
                                </FormColumn>
                            </FormRow>
                            {formik.values.appointmentRequired.toString() === "true" && <FormRow>
                                <FormColumn columns={6}>
                                    <DateField labelText="Preferred date" helpText="Subject to availability" name="appointmentDate" minDate={Moment(formik.values.preferredPickupDate).add(1, "day").toDate()} required={true} onChange={(date, _event) => {
                                        if (date) {
                                            const day = Moment(date).day();

                                            if (day === 0 || day === 6) {
                                                formik.setFieldValue("saturdayDelivery", true);
                                            } else {
                                                formik.setFieldValue("saturdayDelivery", false);
                                            }
                                        } else {
                                            formik.setFieldValue("saturdayDelivery", false);
                                        }
                                    }} />
                                </FormColumn>
                                <FormColumn columns={6}>
                                    <TimeField labelText="Preferred time" name="appointmentTime" required={true} minTime={Moment("09:00", "HH:mm").toDate()} maxTime={Moment("17:00", "HH:mm").toDate()} />
                                </FormColumn>
                            </FormRow>}
                        </FormSection>
                    </FormPage>
                    <FormPage>
                        <FormSection title="Item details">
                            <Collection name="items" createItem={this.createEmptyItem.bind(this)} minItems={1} maxItems={5}>
                                { (_arrayHelpers, item, index) => (<React.Fragment>
                                    <FormRow>
                                        <FormColumn columns={4}>
                                            <SelectField labelText="Type of freight" name={`items[${index}].type`} options={this.state.freightTypeOptions} required={true} />
                                        </FormColumn>
                                        <FormColumn columns={8}>
                                            <InputField labelText="Description of freight" name={`items[${index}].description`} type="text" maxLength={18} />
                                        </FormColumn>
                                    </FormRow>
                                    <FormRow>
                                        <FormColumn columns={3}>
                                            <InputField labelText="Number of pieces" name={`items[${index}].pieceCount`} min={1} max={100000} type="number" required={true} onChange={event => {
                                                formik.setFieldValue(`items[${index}].weight` as any, this.getTotalWeightFromPieceCount(parseFloat(event.target.value), item));
                                            }} />
                                        </FormColumn>
                                        <FormColumn columns={3}>
                                            <InputField labelText="Weight (total)" name={`items[${index}].weight`} min={1} max={19999} step="any" type="number" required={true} onChange={event => {
                                                formik.setFieldValue(`items[${index}].weightPerPiece` as any, this.getWeightPerPieceFromTotal(parseFloat(event.target.value), item) || "");
                                            }} />
                                        </FormColumn>
                                        <FormColumn columns={3}>
                                            <InputField labelText="Weight (per piece)" name={`items[${index}].weightPerPiece`} min={0} max={19999} step="any" type="number" onChange={event => {
                                                formik.setFieldValue(`items[${index}].weight` as any, this.getTotalWeightFromPerPiece(parseFloat(event.target.value), item) || "");
                                            }} />
                                        </FormColumn>
                                        <FormColumn columns={3}>
                                            <SelectField labelText="Units" name={`items[${index}].weightUnitCode`} options={this.state.weightUnitOptions} />
                                        </FormColumn>
                                    </FormRow>
                                    <FormRow>
                                        <FormColumn columns={3}>
                                            <InputField labelText="Length" name={`items[${index}].length`} type="number" required={true} onChange={event => {
                                                let numValue = parseFloat(event.target.value);

                                                formik.setFieldValue("longFreight", !isNaN(numValue) && this.convertDimensionToFeet(numValue, item.dimensionUnitCode || "I") > 12);
                                            }} />
                                        </FormColumn>
                                        <FormColumn columns={3}>
                                            <InputField labelText="Width" name={`items[${index}].width`} type="number" required={true} />
                                        </FormColumn>
                                        <FormColumn columns={3}>
                                            <InputField labelText="Height" name={`items[${index}].height`} type="number" required={true} />
                                        </FormColumn>
                                        <FormColumn columns={3}>
                                            <SelectField labelText="Units" name={`items[${index}].dimensionUnitCode`} options={this.state.dimensionUnitOptions} onChange={event => {
                                                formik.setFieldValue("longFreight", item.length && this.convertDimensionToFeet(item.length, event.target.value) > 12);
                                            }} />
                                        </FormColumn>
                                    </FormRow>
                                    <FormRow>
                                        <FormColumn columns={12} className="mt-n2">
                                            <small className="form-text text-muted">Specify length, width, and height per piece</small>
                                        </FormColumn>
                                    </FormRow>
                                    <FormRow>
                                        <FormColumn columns={6}>
                                            <SelectField labelText="Dangerous goods" name={`items[${index}].dangerousGoods`} options={this.state.yesNoOptions} />
                                        </FormColumn>
                                    </FormRow>
                                    {item.dangerousGoods.toString() === "true" && (<React.Fragment>
                                        <FormRow>
                                            <FormColumn columns={12}>
                                                <div className="alert alert-info mb-0">
                                                    Please refer to <a href="https://wwwapps.tc.gc.ca/Saf-Sec-Sur/3/sched-ann/schedule1form.aspx" target="_blank" rel="noopener noreferrer">Schedule 1 of the TDG Act &amp; Regulations</a> if you require assistance specifying the following information.
                                                </div>
                                            </FormColumn>
                                        </FormRow>
                                        <FormRow>
                                            <FormColumn columns={4}>
                                                <SelectField labelText="Class" name={`items[${index}].dangerousGoodsClassQualifier`} options={this.state.dangerousGoodsClassOptions} required={true} blankOption={true} />
                                            </FormColumn>
                                            <FormColumn columns={4}>
                                                <InputField labelText="U.N. number" name={`items[${index}].dangerousGoodsUNNumber`} type="text" maxLength={4} required={true} />
                                            </FormColumn>
                                            <FormColumn columns={4}>
                                                <SelectField labelText="Packing group" name={`items[${index}].dangerousGoodsPackingGroup`} options={this.state.dangerousGoodsPackingGroupOptions} required={true} blankOption={true} />
                                            </FormColumn>
                                        </FormRow>
                                    </React.Fragment>)}
                                </React.Fragment>)}
                            </Collection>
                        </FormSection>
                    </FormPage>
                    <FormPage>
                        <FormSection title="Freight totals">
                            <FormRow>
                                <FormColumn columns={4}>
                                    <ReadOnlyInputField labelText="Dangerous goods" value={this.getTotalDangerousGoods(formik.values.items)} />
                                </FormColumn>
                                <FormColumn columns={4}>
                                    <ReadOnlyInputField labelText="Total items" value={this.getTotalPieceCount(formik.values.items)} />
                                </FormColumn>
                                <FormColumn columns={4}>
                                    <ReadOnlyInputField labelText="Total cubic feet" value={this.getTotalCubicFeet(formik.values.items)} />
                                </FormColumn>
                            </FormRow>
                            <FormRow>
                                <FormColumn columns={6}>
                                    <ReadOnlyInputField labelText="Total weight (kg)" value={this.getTotalWeightInKilograms(formik.values.items)} />
                                </FormColumn>
                                <FormColumn columns={6}>
                                    <ReadOnlyInputField labelText="Total weight (lbs)" value={this.getTotalWeightInPounds(formik.values.items)} />
                                </FormColumn>
                            </FormRow>
                        </FormSection>
                        <FormSection title="Special instructions">
                            <FormRow>
                                <FormColumn columns={12}>
                                    <TextAreaField name="specialInstructions" rows={4} />
                                </FormColumn>
                            </FormRow>
                        </FormSection>
                        {!!this.getTotalDangerousGoods(formik.values.items) && (
                            <FormSection title="Placards">
                                <div className="alert alert-info" role="alert">
                                    Please contact us. If you are shipping dangerous goods, we require an emergency contact number.
                                </div>
                                <FormRow>
                                    <FormColumn columns={4}>
                                        <SelectField labelText="Offered to carrier?" name="placardOffered" options={this.state.yesNoOptions} />
                                    </FormColumn>
                                    <FormColumn columns={4}>
                                        {this.state.placardTypeOptions &&
                                            <SelectField labelText="Type of placard" name="placardType" options={this.state.placardTypeOptions} blankOption={true} required={formik.values.placardOffered.toString() === "true"} />
                                        }
                                    </FormColumn>
                                    <FormColumn columns={4}>
                                        <InputField labelText="Placard quantity" name="placardQuantity" type="number" required={formik.values.placardOffered.toString() === "true"} />
                                    </FormColumn>
                                </FormRow>
                                <FormRow>
                                    <FormColumn columns={6}>
                                        <InputField labelText="Emergency phone number" name="emergencyResponsePhoneNumber" type="tel" maxLength={80} required={true} inputGroupAppend={<button type="button" className="btn btn-outline-info" onClick={_event => {
                                            modalConfirmation("Confirm CANUTEC registration", "In order to use CANUTEC as your emergency phone number, you must be <a href=\"https://wwwapps.tc.gc.ca/saf-sec-sur/3/SRC-CRS/CANUTEC/index.aspx\" rel=\"noopener noreferer\" target=\"_blank\">registered with CANUTEC</a>. Have you registered with CANUTEC?", () => {
                                                formik.setFieldValue("emergencyResponsePhoneNumber", "613-996-6666");
                                            }, undefined, "No", "Yes", 1052);
                                        }}>Use CANUTEC</button>} />
                                    </FormColumn>
                                    <FormColumn columns={6}>
                                        <InputField labelText="Emergency response plan number" name="emergencyResponsePlanNumber" type="text" maxLength={20} />
                                    </FormColumn>
                                </FormRow>
                            </FormSection>
                        )}
                    </FormPage>
                    <FormPage>
                        <FormSection title="Value added services">
                            <FormRow>
                                <FormColumn columns={12}>
                                    <CheckboxField labelText="Protect from freezing" name="protectFromFreezing" />
                                    <CheckboxField labelText="Flat deck required" name="flatDeckRequired" />
                                    <CheckboxField labelText="Long freight > 12 feet" name="longFreight" disabled={this.anyLongFreight(formik)} />
                                    <CheckboxField labelText="Power tailgate delivery" name="powerTailgateDelivery" />
                                    <CheckboxField labelText="Residential delivery" name="residentialDelivery" />
                                    <CheckboxField labelText="Inside delivery" name="insideDelivery" />
                                    <CheckboxField labelText="Mall delivery" name="mallDelivery" />
                                    <CheckboxField labelText="Saturday delivery" name="saturdayDelivery" disabled={this.isSaturdayDeliveryCheckboxDisabled(formik)} />
                                </FormColumn>
                            </FormRow>
                            {formik.values.flatDeckRequired && <div className="alert alert-info" role="alert">
                                Flat decks are subject to availability - a Rosenau representative will contact you to confirm details about this shipment.
                            </div>}
                        </FormSection>
                        <FormSection title="Reference numbers">
                            <FormRow>
                                <FormColumn columns={6}>
                                    <InputField labelText="Reference number" name="referenceNumber" type="text" maxLength={30} />
                                </FormColumn>
                                <FormColumn columns={6}>
                                    <InputField labelText="Bill of Lading number" name="billOfLadingNumber" type="text" maxLength={30} />
                                </FormColumn>
                            </FormRow>
                            <FormRow>
                                <FormColumn columns={6}>
                                    <InputField labelText="Purchase Order number" name="purchaseOrderNumber" type="text" maxLength={30} />
                                </FormColumn>
                                <FormColumn columns={6}>
                                    <InputField labelText="Rosenau quote number" name="quoteNumber" type="text" pattern="\d*" title="123456" maxLength={7} disabled={disabledForQuoteRequest} />
                                </FormColumn>
                            </FormRow>
                        </FormSection>
                        <FormSection title="Liability information">
                            <div className="alert alert-warning mt-n3 mr-n3 ml-n3" role="alert" style={{borderRadius: 0}}>
                                Unpackaged, used, uncrated glass, granite and overhead doors move at no claim to carrier, owners risk of damage
                                and are not applicable to any coverage.
                                <br />
                                <br />
                                Your shipment is covered under the Bill of Lading Act up to a maximum liability of $2.00 per pound or $4.41 per kilogram
                                computed on the total weight of the shipment unless declared valuation states otherwise.
                            </div>
                            <FormRow>
                                <FormColumn columns={12}>
                                    <label>If additional insurance is required please request below.</label>
                                    <CheckboxField labelText="I require additional insurance on my freight." name="additionalInsurance" />
                                </FormColumn>
                            </FormRow>
                            {formik.values.additionalInsurance && <FormRow>
                                <FormColumn columns={6}>
                                    <InputField labelText="Declared valuation" name="declaredValuation" type="number" min="0" max="50000" required={true} />
                                </FormColumn>
                            </FormRow>}
                        </FormSection>
                        <FormSection title="Conditions">
                            <div className="mb-4">
                                <div className="alert alert-warning mt-n3 mr-n3 mb-n4 ml-n3" role="alert" style={{borderTopLeftRadius: 0, borderTopRightRadius: 0}}>
                                    <ul style={{marginLeft: "-1.25em"}}>
                                        <li>Rosenau Transport Ltd. will not be liable for used equipment, glass, granite, overhead doors and-or uncrated freight. It is shipped at shipper's risk of damage with no declared value. ALL freight must be properly packaged and-or protected for shipping. Claims to carrier movement for damaged goods require that merchandise be in its original shipping packaging.</li>
                                        <li>Rosenau Transport provides heated service only when requested but does not provide temperature controlled environments within specific ranges. We will not be responsible for any penalties relating to temperature controlled claims regardless of circumstances or fault.</li>
                                        <li>Rosenau Transport endeavors to meet all delivery timelines when requested, however, we cannot guarantee appointment times will be met as scheduled. We reserve the right to refuse any associated penalties relating to delay regardless of circumstances or fault.</li>
                                        <li>Rosenau Transport terms will supersede all proceeding agreements or terms as supplied by either the shipper, consignee or 3rd party.</li>
                                    </ul>
                                    <p className="m-0">Find out more at <a href="https://www.rosenau.ca" rel="noopener noreferrer" target="_blank">www.rosenau.ca</a></p>
                                </div>
                            </div>
                        </FormSection>
                    </FormPage>
                    <FormPage onPageLoad={async () => {
                        const response = await Axios.post(`${this.props.bookShipmentEndpointURL}/generatebol`, formik.values);
                        const blob = base64ToBlob(response.data.base64Contents, "application/pdf");
                        const url = URL.createObjectURL(blob);

                        if (!isIE) {
                            PDFObject.embed(url, "#bol-preview", {
                                PDFJS_URL: "/pdfjs/web/viewer.html"
                            });
                        } else {
                            PDFObject.embed(url, "#bol-preview", {
                                PDFJS_URL: "/pdfjs/web/viewer.html",
                                forcePDFJS: true
                            });
                        }
                    }}>
                        <FormSection title="BOL preview">
                            <div id="bol-preview" style={{height: "50vh"}}>
                                <LoadingSpinner />
                            </div>
                            <p className="my-3">Please confirm that the details included in the BOL above are correct. If there are issues, use the "Back" button to return to the appropriate section and correct them. Otherwise, click "Submit" to book your shipment.</p>
                        </FormSection>
                    </FormPage>
                </FormPages> : this.renderConfirmationMessage()}
            </div>
        );
    }

    renderLoadingIndicator(): React.ReactNode {
        // Override in child classes to implement custom loading indicator.

        return <div className="text-center mt-2">
            <div className="w-75 d-inline-block text-center">
                <div className="text-muted mb-1">Loading...</div>
                <div className="progress">
                    <div className="progress-bar progress-bar-striped progress-bar-animated w-100" style={{backgroundColor: "#00a8a6"}} role="progressbar" aria-valuenow={100} aria-valuemin={0} aria-valuemax={100}></div>
                </div>
            </div>
        </div>;
    }

    renderConfirmationMessage(): React.ReactNode {
        // Override in child classes to implement custom submission behavior.

        return <div className="alert alert-success mt-2 confirmation-message">
            Thank you for booking with Rosenau Transport. You will receive 2 emails shortly with confirmation details of your shipment,
            and a pre-populated bill of lading. If you do not receive these emails within 5 minutes, or have a question about your shipment,
            please call us at <strong>1-866-225-9816</strong>.
        </div>;
    }

    createEmptyModel(): WebShipmentRequest {
        return {
            rsn: "",
            contactName: "",
            contactEmail: "",
            contactPhoneNumber: "",
            contactPhoneExtension: "",
            chargeType: 'P',
            customerNumber: "",
            shipperName: "",
            submitterIsContact: false,
            shipperContactName: "",
            shipperEmail: "",
            shipperPhoneNumber: "",
            shipperPhoneExtension: "",
            shipperAddress: "",
            shipperAddress2: "",
            shipperCity: "",
            shipperProvince: "",
            shipperPostalCode: "",
            shipperCountry: "CA",
            preferredPickupDate: "",
            preferredPickupReadyTime: "",
            preferredPickupCloseTime: "",
            consigneeName: "",
            consigneeContactName: "",
            consigneeAddress: "",
            consigneeAddress2: "",
            consigneeCity: "",
            consigneeProvince: "",
            consigneePostalCode: "",
            consigneeCountry: "CA",
            consigneePhoneNumber: "",
            consigneePhoneExtension: "",
            appointmentRequired: false,
            appointmentDate: "",
            appointmentTime: "",
            items: [
                this.createEmptyItem()
            ],
            specialInstructions: "",
            placardOffered: false,
            placardType: "",
            placardQuantity: "",
            emergencyResponsePhoneNumber: "",
            emergencyResponsePlanNumber: "",
            protectFromFreezing: false,
            flatDeckRequired: false,
            longFreight: false,
            powerTailgateDelivery: false,
            residentialDelivery: false,
            insideDelivery: false,
            mallDelivery: false,
            saturdayDelivery: false,
            referenceNumber: "",
            billOfLadingNumber: "",
            purchaseOrderNumber: "",
            quoteNumber: "",
            additionalInsurance: false,
            declaredValuation: "",
            __ss_tk: ""
        };
    }

    createEmptyItem(): WebShipmentRequestItem {
        return {
            type: "Pallet",
            description: "",
            pieceCount: "" as any,
            weight: "" as any,
            weightPerPiece: "" as any,
            weightUnitCode: "L",
            length: "" as any,
            width: "" as any,
            height: "" as any,
            dimensionUnitCode: "I",
            dangerousGoods: "false" as any,
            dangerousGoodsClassQualifier: "" as any,
            dangerousGoodsUNNumber: "",
            dangerousGoodsPackingGroup: ""
        };
    }

    async loadFromQuoteRequest() {
        this.setState({
            loading: true
        });

        try {
            const response = await Axios.get<WebShipmentRequest>(`${this.props.quoteRequestsEndpointURL}/${encodeURIComponent(this.urlSearchParams.get("code") || "")}/webshipmentrequest`);

            const data = response.data;

            data.items.forEach(item => {
                if (item.weight && item.pieceCount) {
                    item.weightPerPiece = this.getWeightPerPieceFromTotal(item.weight, item);
                }

                if (item.length && this.convertDimensionToFeet(item.length, item.dimensionUnitCode) > 12) {
                    data.longFreight = true;
                }
            });

            this.setState({
                data: response.data,
                loaded: true
            });
        } catch (error) {
            if (error?.response?.status === 404) {
                this.setState({
                    loaded: true
                });
            } else {
                this.setState({
                    errorMessage: <React.Fragment>Unexpected error retrieving quote information: <strong>{getErrorMessage(error)}</strong></React.Fragment>,
                    loaded: true
                });
            }
        }
    }

    async submit(values: WebShipmentRequest) {
        if (!this.state.errorMessage) {
            const __ss_tk = Cookies.get("__ss_tk");

            if (__ss_tk) {
                values.__ss_tk = __ss_tk;
            }

            try {
                await Axios.post(`${this.props.bookShipmentEndpointURL}/create`, withoutEmpty(values));

                this.onSubmitComplete();
            } catch (error) {
                if (error?.response?.status === 400) {
                    let errors: string[] = [];

                    if (error.response.data) {
                        for (const key in error.response.data) {
                            errors = [
                                ...errors,
                                error.response.data[key]
                            ];
                        }
                    }

                    this.setState({
                        errorMessage: errors.length ? <React.Fragment>
                            Error submitting the form: {errors.length > 1 ? <ul className="ml-4 mr-0 mt-2 mb-0 p-0">
                                {errors.map(error => <li><strong>{error}</strong></li>)}
                            </ul> : <strong>{errors[0]}</strong>}
                        </React.Fragment> : <React.Fragment>An unknown error has occurred.</React.Fragment>
                    });
                } else {
                    this.setState({
                        errorMessage: <React.Fragment>Unexpected error submitting the form: <strong>{getErrorMessage(error)}</strong></React.Fragment>
                    });
                }
            }
        }

        return Promise.resolve({});
    }

    onSubmitComplete() {
        // Override in child classes to implement custom submission behavior.

        this.setState({
            formComplete: true
        });
    }

    async validate(values: WebShipmentRequest): Promise<FormikErrors<WebShipmentRequest>> {
        this.setState({
            errorMessage: null
        })

        try {
            const response = await Axios.post(`${this.props.bookShipmentEndpointURL}/validate`, values);

            const errors = getFormikErrorsFromValidationResult(response.data);

            if (values.preferredPickupCloseTime && Moment(values.preferredPickupDate, "YYYY-MM-DD").isSame(Moment(), "day") && Moment(values.preferredPickupCloseTime, "HH:mm").toDate() < Moment().add(2, "hour").toDate()) {
                errors["preferredPickupCloseTime"] = (errors["preferredPickupCloseTime"] ? errors["preferredPickupCloseTime"] + "\n" : "") + "At least 2 hours advance notice is required before the close time.";
            }

            return Promise.resolve(errors);
        } catch (error) {
            this.setState({
                errorMessage: <React.Fragment>Unexpected error validating the form: <strong>{getErrorMessage(error)}</strong></React.Fragment>
            })

            return Promise.resolve({});
        }
    }

    convertWeightToPounds(weight: number, unit: string): number {
        let result: number;

        switch (unit) {
            case "L":
                result = weight;
                break;

            case "K":
                result = weight * 2.205;
                break;

            default:
                throw new Error(`${unit} is not a known unit of weight.`);
        }

        return Math.ceil(result);
    }

    convertWeightToKilograms(weight: number, unit: string): number {
        let result: number;

        switch (unit) {
            case "L":
                result = weight / 2.205;
                break;

            case "K":
                result = weight;
                break;
            
            default:
                throw new Error(`${unit} is not a known unit of weight.`);
        }

        return Math.ceil(result);
    }

    convertDimensionToFeet(dimension: number, unit: string): number {
        let result: number;

        switch (unit) {
            case "I":
                result = dimension / 12;
                break;
            
            case "F":
                result = dimension;
                break;
            
            case "C":
                result = dimension / 30.48;
                break;
            
            case "M":
                result = dimension * 3.281;
                break;
            
            default:
                throw new Error(`${unit} is not a known unit of dimension.`);
        }

        return result;
    }

    getWeightPerPieceFromTotal(totalWeight: number | undefined, item: WebShipmentRequestItem): number | undefined {
        if (!item.pieceCount) {
            return undefined;
        }

        if (!totalWeight || isNaN(totalWeight)) {
            return undefined;
        }

        return Math.round((totalWeight / item.pieceCount) * 10) / 10;
    }

    getTotalWeightFromPerPiece(weightPerPiece: number | undefined, item: WebShipmentRequestItem): number | undefined {
        if (!item.pieceCount) {
            return undefined;
        }

        if (!weightPerPiece || isNaN(weightPerPiece)) {
            return undefined;
        }

        return weightPerPiece * item.pieceCount;
    }

    getTotalWeightFromPieceCount(pieceCount: number | undefined, item: WebShipmentRequestItem): number | undefined {
        if (!pieceCount) {
            return undefined;
        }

        if (!item.weightPerPiece) {
            return undefined;
        }

        return item.weightPerPiece * pieceCount;
    }

    getTotalDangerousGoods(items: WebShipmentRequestItem[]): number {
        let result = 0;

        items.forEach(item => {
            if (item.dangerousGoods.toString() === "true") {
                result++;
            }
        });

        return result;
    }

    getTotalPieceCount(items: WebShipmentRequestItem[]): number {
        let result = 0;

        items.forEach(item => {
            result += item.pieceCount || 0;
        });

        return result;
    }

    getTotalCubicFeet(items: WebShipmentRequestItem[]): number {
        let result = 0;

        items.forEach(item => {
            const length = this.convertDimensionToFeet(item.length || 0, item.dimensionUnitCode);
            const width = this.convertDimensionToFeet(item.width || 0, item.dimensionUnitCode);
            const height = this.convertDimensionToFeet(item.height || 0, item.dimensionUnitCode);
            const pieceCount = item.pieceCount || 0;

            result += length * width * height * pieceCount;
        });

        return Math.ceil(result);
    }

    getTotalWeightInKilograms(items: WebShipmentRequestItem[]): number {
        let result = 0;

        items.forEach(item => {
            result += this.convertWeightToKilograms(item.weight || 0, item.weightUnitCode);
        });

        return result;
    }

    getTotalWeightInPounds(items: WebShipmentRequestItem[]): number {
        let result = 0;

        items.forEach(item => {
            result += this.convertWeightToPounds(item.weight || 0, item.weightUnitCode);
        });

        return result;
    }

    isSaturdayDeliveryCheckboxDisabled(formik: FormikProps<WebShipmentRequest>): boolean {
        if (!formik.values.appointmentDate) {
            return false;
        }

        const day = Moment(formik.values.appointmentDate, "YYYY-MM-DD").day();

        if (day === 0 || day === 6) {
            return true;
        }

        return false;
    }

    anyLongFreight(formik: FormikProps<WebShipmentRequest>): boolean {
        let result = false;

        formik.values.items.forEach(item => {
            if (!item.length || result) {
                return;
            }

            if (this.convertDimensionToFeet(item.length, item.dimensionUnitCode) > 12) {
                result = true;
            }
        });

        return result;
    }

    formatAirMilesNumber(airMilesNumber: string | undefined): string | undefined {
        if (!airMilesNumber) {
            return airMilesNumber;
        }

        const result = airMilesNumber.replace(/[^0-9]/g, "");

        if (result.length > 11) {
            return result.substring(0, 11);
        } else {
            return result;
        }
    }
}
